import axios from "axios";
import { API_BASE_PATH } from "../environment";
import { toast } from "react-toastify";

// ✅ Base URL
const BASE_URL = `${API_BASE_PATH}/products`;

// ✅ Product Interface
export interface Product {
  product_id?: string;
  product_name: string;
  product_description: string;
  status?: string;
  product_price: number;
  no_logins?: number | null;
  rate_per_login?: number | null;
  total_no_login: number | null;
  created_at?: string;
  client?: {
    client_id: string;
  };
}
interface DatatableProps {
  columns: any[];
  dataSource: any[];
  loading?: boolean; // ✅ add this if you want loading support
}


// ✅ Auth headers
const getAuthHeaders = () => {
  const token = localStorage.getItem("authToken") || "";
  return {
    Authorization: token,
    Accept: "application/json",
  };
};

const handleAxiosError = (
  error: any,
  fallbackMessage = "Something went wrong"
) => {
  if (axios.isAxiosError(error)) {
    const message =
      error.response?.data?.details ||
      error.response?.data?.message ||
      fallbackMessage;

    toast.error(message);
  } else {
    toast.error(fallbackMessage);
  }
};

// ✅ Get All Products
export const fetchAllProducts = async (): Promise<Product[]> => {
  try {
    const response = await axios.get(`${BASE_URL}/`, {
      headers: getAuthHeaders(),
    });
    return response.data;
  } catch (error: any) {
    const errorMessage =
      error?.response?.data?.details || error?.response?.data?.message || "Failed to fetch Products";
    toast.error(errorMessage);
    return [];
  }
};

// ✅ Get All Products
export const fetchAllProductsActive = async (): Promise<Product[]> => {
  try {
    const response = await axios.get(`${BASE_URL}/active/only/`, {
      headers: getAuthHeaders(),
    });
    return response.data;
  } catch (error: any) {
    const errorMessage =
      error?.response?.data?.details || error?.response?.data?.message || "Failed to fetch Products";
    toast.error(errorMessage);
    return [];
  }
};

// ✅ Get Product by ID
export const fetchProductById = async (
  product_id: string
): Promise<Product | null> => {
  try {
    const response = await axios.get(`${BASE_URL}/${product_id}`, {
      headers: getAuthHeaders(),
    });
    return response.data;
  } catch (error: any) {
    const errorMessage =
      error?.response?.data?.details || error?.response?.data?.message || "Failed to fetch Product ID";
    toast.error(errorMessage);
    return null;
  }
};

// ✅ Create Product
export const createProduct = async (
  data: Product
): Promise<Product | null> => {
  try {
    const formData = new URLSearchParams();
    formData.append("product_name", data.product_name);
    formData.append("product_description", data.product_description);
    formData.append("status", data.status || "Active");
    formData.append("product_price", data.product_price.toString());
    formData.append("no_logins", data.no_logins?.toString() || "0");
    formData.append("rate_per_login", data.rate_per_login?.toString() || "0");

    const response = await axios.post(`${BASE_URL}/`, formData, {
      headers: {
        "Content-Type": "application/x-www-form-urlencoded",
        ...getAuthHeaders(),
      },
    });

    toast.success("Product created successfully!");
    return response.data;
  } catch (error: any) {
    const errorMessage =
      error?.response?.data?.details || error?.response?.data?.message || "Failed to create Product";
    toast.error(errorMessage);
    return null;
  }
};

// ✅ Update Product
export const updateProduct = async (
  product_id: string,
  data: Product
): Promise<Product | null> => {
  try {
    const formData = new URLSearchParams();
    formData.append("product_name", data.product_name);
    formData.append("product_description", data.product_description);
    formData.append("status", data.status || "Active");
    formData.append("product_price", data.product_price.toString());
    formData.append("no_logins", data.no_logins?.toString() || "0");
    formData.append("rate_per_login", data.rate_per_login?.toString() || "0");

    const response = await axios.put(`${BASE_URL}/${product_id}`, formData, {
      headers: {
        "Content-Type": "application/x-www-form-urlencoded",
        ...getAuthHeaders(),
      },
    });

    toast.success("Product updated successfully!");
    return response.data;
  } catch (error: any) {
    const errorMessage =
      error?.response?.data?.details || error?.response?.data?.message || "Failed to update product.";
    toast.error(errorMessage);
    return null;
  }
};

// ✅ Delete Product
export const deleteProduct = async (product_id: string): Promise<boolean> => {
  try {
    await axios.delete(`${BASE_URL}/${product_id}`, {
      headers: getAuthHeaders(),
    });
    toast.success("Product deleted successfully!");
    return true;
  } catch (error: any) {
    const errorMessage =
      error?.response?.data?.details || error?.response?.data?.message || "Failed to delete product.";
    toast.error(errorMessage);
    return false;
  }
};

// ✅ Export all Products – Excel
export const exportProductExcel = async (): Promise<Blob> => {
  try {
    const res = await axios.get(
      `${API_BASE_PATH}/product_export_all_excel`,
      {
        responseType: "blob",
        headers: getAuthHeaders(),
      }
    );

    toast.success("Products exported as Excel successfully");
    return res.data;
  } catch (error) {
    handleAxiosError(error, "Failed to export products (Excel)");
    throw error;
  }
};

// ✅ Export all Products – PDF
export const exportProductPDF = async (): Promise<Blob> => {
  try {
    const res = await axios.get(
      `${API_BASE_PATH}/product_export_all_pdf`,
      {
        responseType: "blob",
        headers: getAuthHeaders(),
      }
    );

    toast.success("Products exported as PDF successfully");
    return res.data;
  } catch (error) {
    handleAxiosError(error, "Failed to export products (PDF)");
    throw error;
  }
};

/* =====================================================
   EXPORT – SELECTED
===================================================== */

// ✅ Export selected Products – Excel
export const exportSelectedProductExcel = async (
  ids: string[]
): Promise<Blob> => {
  try {
    const res = await axios.get(
      `${API_BASE_PATH}/product_export_selected_excel`,
      {
        params: { ids: ids.join(",") },
        responseType: "blob",
        headers: getAuthHeaders(),
      }
    );

    toast.success("Selected products exported as Excel successfully");
    return res.data;
  } catch (error) {
    handleAxiosError(
      error,
      "Failed to export selected products (Excel)"
    );
    throw error;
  }
};

// ✅ Export selected Products – PDF
export const exportSelectedProductPDF = async (
  ids: string[]
): Promise<Blob> => {
  try {
    const res = await axios.get(
      `${API_BASE_PATH}/product_export_selected_pdf`,
      {
        params: { ids: ids.join(",") },
        responseType: "blob",
        headers: getAuthHeaders(),
      }
    );

    toast.success("Selected products exported as PDF successfully");
    return res.data;
  } catch (error) {
    handleAxiosError(
      error,
      "Failed to export selected products (PDF)"
    );
    throw error;
  }
};

/* =====================================================
   BULK DELETE
===================================================== */

// ✅ Bulk delete Products
export const deleteProductBulk = async (
  ids: string[]
): Promise<void> => {
  try {
    const res = await axios.delete(
      `${API_BASE_PATH}/product_bulk_delete`,
      {
        params: { ids: ids.join(",") },
        headers: getAuthHeaders(),
      }
    );

    // toast.success(
    //   res.data?.message || "Products deleted successfully"
    // );
  } catch (error: any) {
    if (error?.response?.status === 403) {
      console.error("You are not authorized to delete products.");
    } else {
      handleAxiosError(error, "Failed to delete products");
    }
    throw error;
  }
};

/* =====================================================
   IMPORT – EXCEL
===================================================== */

export interface ProductImportResult {
  message: string;
  added: {
    product_name: string;
    product_description: string;
    product_price: number;
    status: string;
  }[];
  skipped: {
    row: number;
    product_name: string;
    reason: string;
  }[];
}

// ✅ Import Product Excel
export const importProductExcel = async (
  file: File
): Promise<ProductImportResult> => {
  try {
    const formData = new FormData();
    formData.append("file", file);

    const res = await axios.post(
      `${API_BASE_PATH}/product_import_excel`,
      formData,
      {
        headers: {
          ...getAuthHeaders(),
          "Content-Type": "multipart/form-data",
        },
      }
    );

    toast.success(res.data?.message || "Product import completed");
    return res.data;
  } catch (error) {
    handleAxiosError(error, "Failed to import products");
    throw error;
  }
};